import { namespace } from '@common-config/constants';
import moment from 'moment';
import { formatInLanguage } from '@common/config/moment-locales';
import React from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { COUNT_KEY, emptyPeriodText, type EmptyPeriodCopy } from './empty-state-copy';
import styles from './calendar-grid.module.scss';
import type { CalendarGridView } from './types';

interface Props {
    view: CalendarGridView;
    date: Date;
    count: number;
    /** What an EMPTY period says — decided by `emptyPeriodCopy`, never here, so the toolbar
     *  and the month grid's centred caption cannot disagree. */
    emptyCopy: EmptyPeriodCopy;
    /** Reveal the sends that already went out. Offered here in the week and day views; the
     *  month view offers it on its centred caption instead, where the eye lands. */
    onShowSent?: () => void;
    /** The range on screen is still loading (already smoothed by useDelayedBusy: no blink). */
    busy?: boolean;
    /** What went out in this range failed to load — the picture may be incomplete. */
    loadFailed?: boolean;
    onRetry?: () => void;
    isRtl: boolean;
    onView: (view: CalendarGridView) => void;
    onNavigate: (date: Date) => void;
    onCreate: () => void;
}

const VIEWS: CalendarGridView[] = ['month', 'week', 'day'];

// The count line and the empty line both name the period they talk about, and
// the filtered/unfiltered choice lives in `empty-state-copy` so the toolbar and
// the month grid's centred caption cannot drift apart.

const VIEW_KEY: Record<CalendarGridView, string> = {
    month: 'calendar.month',
    week: 'calendar.week',
    day: 'calendar.day',
};

// `language` is REQUIRED, not optional: a bare moment() reads the GLOBAL locale, which is
// set from i18next's `languageChanged` handler and so loses the race against a component
// that renders first. That is why the calendar's own heading — the most prominent label on
// the primary screen — still read "September 2026" inside a fully-Hebrew app. Taking the
// language as a parameter makes the output a pure function of the render's own props, and
// makes the compiler point at every call site.
function rangeLabel(view: CalendarGridView, date: Date, language: string): string {
    const fmt = (value: Date | moment.Moment, pattern: string) =>
        formatInLanguage(moment(value).valueOf(), language, pattern);
    if (view === 'month') return fmt(date, 'MMMM YYYY');
    if (view === 'day') return fmt(date, 'dddd, MMM D');
    const start = moment(date).startOf('week');
    const end = start.clone().add(6, 'days');
    return start.isSame(end, 'month')
        ? `${fmt(start, 'MMM D')} – ${fmt(end, 'D')}`
        : `${fmt(start, 'MMM D')} – ${fmt(end, 'MMM D')}`;
}

export const CalendarToolbarV2: React.FC<Props> = ({ view, date, count, emptyCopy, onShowSent, busy, loadFailed, onRetry, isRtl, onView, onNavigate, onCreate }) => {
    const { t, i18n } = useTranslation(namespace);

    const step = (delta: number) => {
        const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';
        onNavigate(moment(date).add(delta, unit).toDate());
    };

    // In RTL the leading arrow still means "back in time"; only the glyph flips.
    const backGlyph = isRtl ? '›' : '‹';
    const forwardGlyph = isRtl ? '‹' : '›';

    return (
        <div className={styles.toolbar}>
            <button type="button" className={styles.navBtn} onClick={() => step(-1)} aria-label={t('calendar.previous')}>{backGlyph}</button>
            <button type="button" className={styles.navBtn} onClick={() => step(1)} aria-label={t('calendar.next')}>{forwardGlyph}</button>
            <button type="button" className={styles.todayBtn} onClick={() => onNavigate(new Date())}>{t('calendar.today')}</button>

            <div className={styles.range}>
                {rangeLabel(view, date, i18n.language)}
                {/* Name the period the number belongs to: "9 sends" alone leaves the
                    user to guess whether it means this month, this week or everything. */}
                <small>
                    {/* While the range is loading an empty period says nothing yet (a no-break
                        space keeps the line's height): "nothing scheduled" before the data is in
                        would be a claim, and the centre already says it is loading. */}
                    {count === 0
                        ? (busy ? ' ' : emptyPeriodText(t, emptyCopy))
                        : t(COUNT_KEY[view], { count })}
                    {count === 0 && !busy && emptyCopy.kind === 'nothing' && emptyCopy.actionKey && onShowSent && (
                        <button type="button" className={styles.inlineAction} onClick={onShowSent}>
                            {t(emptyCopy.actionKey)}
                        </button>
                    )}
                </small>
            </div>

            {/* The toolbar's empty middle answers "why am I not seeing it yet?" while the range
                on screen is loading (paging months, switching view, turning on Show sent), and
                says so when it could not load. Positioned INSIDE the spacer, out of flow, so
                appearing never moves the title or the buttons. The sentence also goes to a
                polite live region — the spinner itself is decoration. */}
            <div className={styles.spacer}>
                {busy && (
                    <span className={styles.toolbarBusy}>
                        <Loader2 className={`animate-spin ${styles.toolbarBusyIcon}`} aria-hidden="true" />
                        {t('calendar.grid.loadingSends')}
                    </span>
                )}
                {!busy && loadFailed && (
                    <span className={styles.toolbarBusy}>
                        {t('calendar.grid.loadSentFailed')}
                        {onRetry && (
                            <button type="button" className={styles.inlineAction} onClick={onRetry}>
                                {t('calendar.grid.retry')}
                            </button>
                        )}
                    </span>
                )}
                <span className={styles.srOnly} aria-live="polite">
                    {busy ? t('calendar.grid.loadingSends') : loadFailed ? t('calendar.grid.loadSentFailed') : ''}
                </span>
            </div>

            {/* The only permanent way in. Creating used to require discovering
                that hovering a cell reveals a "+", which does not exist at all
                on a touch screen — so a new user with an empty calendar had
                nothing to click. */}
            <button type="button" className={styles.createBtn} onClick={onCreate}>
                {t('calendar.grid.newMessage')}
            </button>

            <div className={styles.segmented}>
                {VIEWS.map(v => (
                    <button
                        key={v}
                        type="button"
                        className={styles.segBtn}
                        aria-pressed={view === v}
                        onClick={() => onView(v)}
                    >
                        {t(VIEW_KEY[v])}
                    </button>
                ))}
            </div>
        </div>
    );
};
